import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, ChevronRight, Map as MapIcon, Search, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import BeachCard from "@/components/BeachCard";
import BeachMap from "@/components/BeachMap";
import Footer from "@/components/Footer";
import SeoHead from "@/components/SeoHead";
import { getBeaches, getCities } from "@/lib/api";
import { toast } from "sonner";

const StatePage = () => {
  const { stateSlug } = useParams();
  const [beaches, setBeaches] = useState([]);
  const [allBeaches, setAllBeaches] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stateName, setStateName] = useState("");
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [beachesData, allBeachesData, citiesData] = await Promise.all([
          getBeaches({ state: stateSlug, per_page: 12 }),
          getBeaches({ state: stateSlug, per_page: 100 }),
          getCities(stateSlug, 30),
        ]);

        setBeaches(beachesData.beaches || []);
        setAllBeaches(allBeachesData.beaches || []);
        setTotal(beachesData.total || 0);
        setCities(citiesData || []);

        if (beachesData.beaches?.[0]) {
          setStateName(beachesData.beaches[0].state);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [stateSlug]);

  const formatStateName = (slug) => {
    return slug
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const displayStateName = stateName || formatStateName(stateSlug);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Beach Access in ${displayStateName}`,
          text: `Find ${total} public beach access points in ${displayStateName}`,
          url: url,
        });
      } catch (err) {
        console.log("Share cancelled");
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!");
    }
  };

  const BEACH_IMAGE = "/images/hero/beach-default.jpg";

  const stateImages = {
    "florida": "/images/states/florida.jpg",
    "california": "/images/states/california.jpg",
    "massachusetts": "/images/states/massachusetts.jpg",
    "new-jersey": "/images/states/new-jersey.jpg",
    "new-york": "/images/states/new-york.jpg",
    "texas": "/images/states/texas.jpg",
    "north-carolina": "/images/states/north-carolina.jpg",
    "south-carolina": "/images/states/south-carolina.jpg",
    "michigan": "/images/states/michigan.jpg",
    "hawaii": "/images/states/hawaii.jpg",
    "minnesota": "/images/states/minnesota.jpg",
    "washington": "/images/states/washington.jpg",
    "wisconsin": "/images/states/wisconsin.jpg",
    "rhode-island": "/images/states/rhode-island.jpg",
    "maine": "/images/states/maine.jpg",
    "idaho": "/images/states/idaho.jpg",
    "illinois": "/images/states/illinois.jpg",
    "oregon": "/images/states/oregon.jpg",
    "connecticut": "/images/states/connecticut.jpg",
    "virginia": "/images/states/virginia.jpg",
    "georgia": "/images/states/georgia.jpg",
    "new-hampshire": "/images/states/new-hampshire.jpg",
    "alabama": "/images/states/alabama.jpg",
    "mississippi": "/images/states/mississippi.jpg",
    "delaware": "/images/states/delaware.jpg",
    "maryland": "/images/states/maryland.jpg",
    "louisiana": "/images/states/louisiana.jpg",
    "alaska": "/images/states/alaska.jpg",
    "arizona": "/images/states/arizona.jpg",
  };

  // Get state-specific hero image
  const getStateImage = () => {
    return stateImages[stateSlug] || BEACH_IMAGE;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-amber-50" data-testid="state-page">
      <SeoHead
        title={`${total} Public Beach Access Points in ${displayStateName}`}
        description={`Complete guide to ${total} free public beach access points across ${cities.length} cities in ${displayStateName}. Find beaches with parking, restrooms, showers, and ADA accessibility. Browse by city, view maps, read reviews, and get directions to every beach entrance in ${displayStateName}.`}
        path={`/state/${stateSlug}`}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://publicbeachaccess.com/" },
          { "@type": "ListItem", "position": 2, "name": displayStateName, "item": `https://publicbeachaccess.com/state/${stateSlug}` }
        ]
      })}} />
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Link to="/" className="hover:text-[#0EA5E9]">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/search" className="hover:text-[#0EA5E9]">All States</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-800 font-medium">{displayStateName}</span>
          </nav>

          {/* Title Row */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-800 font-['Outfit'] tracking-tight">
                🏖️ Beach Access in {displayStateName}
              </h1>
              <p className="text-gray-500 mt-1">
                {total} public beach access points across {cities.length} cities
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handleShare}
                className="border-2 border-gray-200 hover:bg-gray-50"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              <Link to={`/map?state=${stateSlug}`}>
                <Button className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-semibold">
                  <MapIcon className="w-4 h-4 mr-2" />
                  View Map
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Blue Divider */}
      <div className="h-2 bg-[#0EA5E9]" />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Left Column - Image & Overview */}
          <div className="lg:col-span-3 space-y-6">
            {/* State Image */}
            <div className="relative rounded-2xl overflow-hidden shadow-lg">
              <img
                src={getStateImage()}
                alt={`Beach in ${displayStateName}`}
                className="w-full h-[300px] sm:h-[400px] object-cover"
              />
              <div className="absolute top-4 left-4">
                <span className="bg-[#10B981] text-white px-4 py-2 rounded-lg font-bold text-sm shadow-lg">
                  🏖️ {total} Beach Access Points
                </span>
              </div>
            </div>

            {/* Overview */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mb-4">Overview</h2>
              <p className="text-gray-600 leading-relaxed">
                Discover {total} public beach access points throughout {displayStateName}. 
                From popular destinations to hidden gems, find the perfect beach entrance with 
                parking information, amenities, and directions. Whether you're looking for 
                beaches with restrooms, showers, or wheelchair accessibility, our directory 
                helps you find the right spot for your beach day.
              </p>
            </div>

            {/* Cities Grid */}
            {cities.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mb-4">
                  Cities with Beach Access
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {cities.slice(0, 12).map((city) => (
                    <Link
                      key={city.slug}
                      to={`/state/${stateSlug}/${city.slug}`}
                      className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl hover:bg-[#DBEAFE] hover:text-[#0EA5E9] transition-colors group"
                    >
                      <MapPin className="w-4 h-4 text-[#0EA5E9] group-hover:scale-110 transition-transform" />
                      <span className="font-medium text-sm truncate">{city.name}</span>
                      <span className="text-xs text-gray-400 ml-auto">({city.beach_count})</span>
                    </Link>
                  ))}
                </div>
                {cities.length > 12 && (
                  <div className="mt-4 text-center">
                    <Link to={`/search?state=${stateSlug}`}>
                      <Button variant="outline" size="sm">
                        View All {cities.length} Cities
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Featured Beaches */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-800 font-['Outfit']">
                  Featured Beach Access
                </h2>
                <Link
                  to={`/search?state=${stateSlug}`}
                  className="text-[#0EA5E9] font-semibold text-sm hover:underline flex items-center"
                >
                  View All {total}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </div>

              {loading ? (
                <div className="grid sm:grid-cols-2 gap-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="h-32 bg-gray-100 animate-pulse rounded-xl" />
                  ))}
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {beaches.slice(0, 6).map((beach) => (
                    <BeachCard key={beach.id} beach={beach} compact />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Map & Quick Links */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Search */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mb-4">Quick Search</h2>
              <Link to={`/search?state=${stateSlug}`}>
                <Button className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-semibold h-12 mb-3">
                  <Search className="w-5 h-5 mr-2" />
                  Search All {displayStateName} Beaches
                </Button>
              </Link>
              <Link to={`/map?state=${stateSlug}`}>
                <Button variant="outline" className="w-full border-2 border-gray-200 hover:bg-gray-50 h-12">
                  <MapIcon className="w-5 h-5 mr-2" />
                  Explore on Map
                </Button>
              </Link>
            </div>

            {/* Map Preview */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mb-4">Location</h2>
              <div className="h-[300px] rounded-xl overflow-hidden">
                {allBeaches.length > 0 ? (
                  <BeachMap beaches={allBeaches} />
                ) : (
                  <div className="h-full bg-gray-100 flex items-center justify-center text-gray-500">
                    Loading map...
                  </div>
                )}
              </div>
            </div>

            {/* Top Cities */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mb-4">Top Destinations</h2>
              <div className="space-y-2">
                {cities.slice(0, 5).map((city, index) => (
                  <Link
                    key={city.slug}
                    to={`/state/${stateSlug}/${city.slug}`}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-[#DBEAFE] transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-[#0EA5E9] text-white rounded-full flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </span>
                      <span className="font-medium text-gray-800 group-hover:text-[#0EA5E9]">{city.name}</span>
                    </div>
                    <span className="text-sm text-gray-500">{city.beach_count} beaches</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* SEO Articles - All States */}
            {cities.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-800 font-['Outfit'] mb-4">
                  Popular Guides
                </h2>
                <div className="space-y-2">
                  {cities.slice(0, 3).map((city) => (
                    <Link
                      key={`best-${city.slug}`}
                      to={`/${stateSlug}/best-beach-access/${city.slug}`}
                      className="flex items-center justify-between p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl hover:from-amber-100 hover:to-orange-100 transition-colors group"
                    >
                      <span className="font-medium text-gray-800 text-sm">Best in {city.name}</span>
                      <ChevronRight className="w-4 h-4 text-orange-500 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default StatePage;
